import { getDecryptedUserDetails } from "@ts/utils";

declare global {
    interface Window {
        heap: {
            identify: (
                email: string,
                userProperties?: Record<string, any>
            ) => void;
            addUserProperties: (properties: Record<string, any>) => void;
            resetIdentity: () => void;
        };
    }
}

export interface HeapUserProperties {
    email: string;
    firstName: string;
    lastName: string;
    user_id: number;
    last_login?: string;
    country?: string;
    timezone?: string;
}

/**
 * Identifies a user in Heap analytics with their properties
 * @param userId - Unique identifier for the user
 * @param userProperties - Additional user properties to set
 */
export const identifyUserInHeap = (
    email: string,
    userProperties?: HeapUserProperties
) => {
    if (typeof window !== "undefined" && window.heap) {
        try {
            window.heap.identify(email, userProperties);
            // console.log("User identified in Heap:", email);
        } catch (error) {
            console.error("Error identifying user in Heap:", error);
        }
    } else {
        console.warn("Heap not available for user identification");
    }
};

/**
 * Adds additional user properties to Heap analytics
 * @param properties - User properties to add
 */
export const addUserPropertiesToHeap = (properties: Record<string, any>) => {
    if (typeof window !== "undefined" && window.heap) {
        try {
            window.heap.addUserProperties(properties);
            // console.log("User properties added to Heap:", properties);
        } catch (error) {
            console.error("Error adding user properties to Heap:", error);
        }
    } else {
        console.warn("Heap not available for adding user properties");
    }
};

/**
 * Resets the user identity in Heap analytics (useful for logout)
 */
export const resetHeapIdentity = () => {
    if (typeof window !== "undefined" && window.heap) {
        try {
            window.heap.resetIdentity();
            // console.log("Heap identity reset");
        } catch (error) {
            console.error("Error resetting Heap identity:", error);
        }
    }
};

/**
 * Identifies user in Heap using data from sessionStorage and localStorage
 * This function should be called after user authentication is complete
 */
export const identifyUserFromStorage = () => {
    try {
        // Get user data from localStorage (decrypted)
        const userData = getDecryptedUserDetails();

        if (!userData || !userData.email) {
            console.warn("No user data available for Heap identification");
            return;
        }

        // Get chatbot data from sessionStorage (if available)
        const chatbotData = sessionStorage.getItem("chatbot");
        const chatbotInfo = chatbotData ? JSON.parse(chatbotData) : {};

        // Prepare user properties for Heap
        const heapUserProperties: HeapUserProperties = {
            email: userData.email || chatbotInfo.email,
            firstName: userData.first_name,
            lastName: userData.last_name,
            user_id: userData.user_id,
            country: userData.country,
            timezone: userData.timezone,
        };

        // Identify user in Heap
        identifyUserInHeap(userData.email.toString(), heapUserProperties);

        return heapUserProperties;
    } catch (error) {
        console.error("Error identifying user from storage in Heap:", error);
    }
};
